import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { MoreVertical, Pencil, Archive, ArchiveRestore } from 'lucide-react'
import type { SharedProps } from '@/types'
import type CrmFundraisingPaymentMethod from '@/types/serializers/CrmFundraisingPaymentMethod'
import { EditPaymentMethodSheet } from './EditPaymentMethodSheet'

export function PaymentMethodActions({
  method,
}: {
  method: CrmFundraisingPaymentMethod
}) {
  const { auth } = usePage<{ auth: SharedProps['auth'] }>().props
  const [editOpen, setEditOpen] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)

  if (auth.user?.role !== 'space_admin') return null

  function handleArchive() {
    router.patch(`/fundraising/payment_methods/${method.id}/archive`, {}, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setConfirmArchive(false)
        toast.success('Mode de paiement archive avec succes')
      },
    })
  }

  function handleRestore() {
    router.patch(`/fundraising/payment_methods/${method.id}/restore`, {}, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => toast.success('Mode de paiement restaure avec succes'),
    })
  }

  return (
    <div className="text-right" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-7 shrink-0">
            <MoreVertical className="size-4" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {method.discarded ? (
            <DropdownMenuItem onClick={handleRestore} className="flex items-center gap-2">
              <ArchiveRestore className="size-4" />
              Restaurer
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onClick={() => setEditOpen(true)} className="flex items-center gap-2">
                <Pencil className="size-4" />
                Renommer
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setConfirmArchive(true)}
                className="flex items-center gap-2 text-destructive focus:text-destructive"
              >
                <Archive className="size-4" />
                Archiver
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <EditPaymentMethodSheet
        method={editOpen ? method : null}
        onOpenChange={setEditOpen}
      />

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver ce mode de paiement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Il disparaitra des listes et des formulaires. Vous pourrez le
              restaurer plus tard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archiver</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
